import { getBrowserLanguage } from 'functions/webUtilsTypescript';
import i18n, { isLocaleLoaded, loadLocale } from 'hooks/useI18n';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { SUPPORTED_LANGUAGES } from 'redhotpie-shared-lib/constants/constants';

interface I18nContextType {
  locale: string;
  setLanguage: (lang: string) => void;
  /** Bumped each time a dictionary finishes loading so consumers re-render with the new strings. */
  dictionaryVersion: number;
}

export const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
}

export const LANG_PARAM = 'lang';
// Same cookie the static wwwroot pages read (see google-translate.js) — this
// keeps the language preference shared between the React app and those
// server-rendered pages instead of drifting out of sync.
const LOCALE_COOKIE_NAME = '.AspNetCore.Culture';
const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year, so the choice survives across sessions like localStorage did.

// Region tag ASP.NET Core's culture cookie expects per supported locale
// (matches the `?lang=` links on the static pages, e.g. `?lang=es-ES`).
const CULTURE_BY_LOCALE: Record<string, string> = {
  en: 'en-GB',
  es: 'es-ES',
  de: 'de-DE',
  nl: 'nl-NL',
  it: 'it-IT',
  fr: 'fr-FR',
  pt: 'pt-PT',
  th: 'th-TH',
  id: 'id-ID',
  vi: 'vi-VN',
};

// Accepts region-tagged values (es-ES, zh-TW) — only the primary subtag
// selects the dictionary.
export const toLangCode = (value: string) => value.split('-')[0].toLowerCase();

function getCookie(name: string): string | undefined {
  const pattern = new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`);
  const match = document.cookie.match(pattern);
  return match ? decodeURIComponent(match[1]) : undefined;
}

// ASP.NET Core's culture cookie value looks like: c=es-ES|uic=es-ES
function localeFromCultureCookie(raw: string): string | undefined {
  const cultureMatch = raw.match(/(?:^|\|)c=([^|]+)/i);
  return cultureMatch ? toLangCode(cultureMatch[1]) : undefined;
}

function setLocaleCookie(locale: string): void {
  const culture = CULTURE_BY_LOCALE[locale] ?? locale;
  const value = `c=${culture}|uic=${culture}`;
  document.cookie = `${LOCALE_COOKIE_NAME}=${encodeURIComponent(value)}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE}`;
}

// Keeps <html lang> in sync with the active locale's full culture tag (e.g.
// "de-DE") — the `*:lang(de-DE)` CSS overrides in styles.scss (longer
// translated copy needing more room) only ever match against this attribute.
function setHtmlLang(locale: string): void {
  document.documentElement.lang = CULTURE_BY_LOCALE[locale] ?? locale;
}

// Default locale on first render, in priority order: the ?lang= URL param
// (shareable/bookmarked links), the last language the user explicitly
// picked (cookie), then the visitor's browser language. Falls back to the
// i18n instance's compiled-in default (English) when none match a
// dictionary we actually ship.
function resolveInitialLocale(): string {
  const paramLang = new URLSearchParams(window.location.search).get(LANG_PARAM);
  if (paramLang) {
    const code = toLangCode(paramLang);
    if (SUPPORTED_LANGUAGES.includes(code)) {
      // An explicit ?lang= link is as deliberate a choice as clicking the
      // selector — persist it so it's remembered on the next visit too.
      setLocaleCookie(code);
      return code;
    }
  }

  const rawCookie = getCookie(LOCALE_COOKIE_NAME);
  const stored = rawCookie && localeFromCultureCookie(rawCookie);
  if (stored && SUPPORTED_LANGUAGES.includes(stored)) return stored;

  // Browser-language fallback is intentionally NOT persisted: it's a passive
  // per-visit default, not an explicit preference, so it stays live to a
  // later change in the visitor's browser language until they pick one.
  const browserCode = getBrowserLanguage();
  if (SUPPORTED_LANGUAGES.includes(browserCode)) return browserCode;

  return i18n.defaultLocale;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const [locale, setLocale] = useState<string>(() => {
    const resolved = resolveInitialLocale();
    setHtmlLang(resolved);
    return resolved;
  });
  // Non-English dictionaries are lazy-loaded chunks (see hooks/useI18n.ts).
  // `ready` is false while the dictionary for `locale` is still downloading;
  // children are not rendered until it lands so users never see a flash of
  // English text before their language appears.
  // Only the very first render is blocked. When the user switches language
  // later, the current strings stay on screen until the new chunk arrives.
  const [ready, setReady] = useState<boolean>(() => isLocaleLoaded(locale));
  const [dictionaryVersion, setDictionaryVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const apply = () => {
      if (cancelled) return;
      i18n.locale = locale;
      setReady(true);
      setDictionaryVersion(v => v + 1);
    };
    if (isLocaleLoaded(locale)) {
      apply();
    } else {
      loadLocale(locale)
        .catch(() => {
          // Chunk failed to download: keep the app usable with the English fallback.
        })
        .then(apply);
    }
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const setLanguage = useCallback((newLocale: string) => {
    setLocale(newLocale);
    setLocaleCookie(newLocale);
    setHtmlLang(newLocale);
  }, []);

  const value = useMemo(() => ({ locale, setLanguage, dictionaryVersion }), [locale, setLanguage, dictionaryVersion]);
  if (!ready) return null;
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = useContext(I18nContext);

  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }

  return context;
};

export const I18nRerenderBoundary = ({ children }: { children: () => ReactNode }) => {
  useI18n();

  return <React.Fragment>{children()}</React.Fragment>;
};
